import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowRight, RotateCcw, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";

export const ExamResult = () => {
  const { examId, attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    fetchResult();
  }, [attemptId]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API}/exam-history/${attemptId}`);
      setAttempt(response.data);
    } catch (error) {
      toast.error("Erro ao carregar resultado");
      navigate("/exams");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground font-mono text-sm uppercase tracking-widest">
          Carregando...
        </div>
      </div>
    );
  }

  if (!attempt) return null;

  const scoreClass = attempt.score >= 70 ? "score-good" : attempt.score >= 50 ? "score-medium" : "score-bad";

  return (
    <div className="min-h-screen bg-white" data-testid="exam-result-page">
      {/* Header */}
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-center">
          <img src={LOGO_URL} alt="AESTHA" className="h-8 invert" />
        </div>
      </header>

      {/* Result */}
      <main className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center animate-slideUp">
          {/* Score Circle */}
          <div className={`score-circle mx-auto mb-8 ${scoreClass}`} data-testid="score-display">
            {attempt.score}%
          </div>

          <h1 className="font-serif text-3xl mb-2" data-testid="exam-title">
            {attempt.exam_title}
          </h1>
          
          <p className="text-muted-foreground mb-8">
            Você acertou {attempt.correct_answers} de {attempt.total_questions} questões
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              onClick={() => setShowAnswers(!showAnswers)}
              variant="outline"
              className="btn-secondary"
              data-testid="toggle-answers"
            >
              {showAnswers ? "Ocultar respostas" : "Ver respostas"}
            </Button>
            
            <Link to={`/exams/${examId}`}>
              <Button className="btn-primary w-full sm:w-auto" data-testid="retry-button">
                <RotateCcw size={16} className="mr-2" />
                Refazer
              </Button>
            </Link>
          </div>

          {/* Answers Review */}
          {showAnswers && (
            <div className="text-left space-y-6 animate-slideUp" data-testid="answers-review">
              {attempt.answers.map((answer, index) => (
                <div
                  key={answer.question_id}
                  className={`p-6 border ${answer.is_correct ? "answer-correct" : "answer-wrong"}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    {answer.is_correct ? (
                      <CheckCircle className="text-green-600 shrink-0" size={20} />
                    ) : (
                      <XCircle className="text-red-600 shrink-0" size={20} />
                    )}
                    <div>
                      <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                        Questão {index + 1}
                      </p>
                      <p className="font-serif text-lg">{answer.question_text}</p>
                    </div>
                  </div>

                  {answer.explanation && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        <strong>Explicação:</strong> {answer.explanation}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Back to Dashboard */}
          <div className="mt-12 pt-8 border-t">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                <Home size={16} className="mr-2" />
                Voltar ao Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExamResult;
